import type { AxiosProgressEvent } from 'axios';
import apiClient from './client';
import {
  getUploadMultipartConfig,
  shouldUseChunkedUpload,
  uploadFileInChunks,
} from './chunked-upload';
import { uploadDirectToOss } from './direct-upload';
import { keysToCamelCase } from '@/lib/api-utils';

export interface AttachmentUploadResponse {
  id?: string;
  attachmentId?: string;
  fileUrl: string;
  fullUrl?: string;
  mimeType?: string;
  fileSize?: number;
  width?: number | null;
  height?: number | null;
  duration?: string | number | null;
  sourceAttachmentId?: string | null;
}

export interface AttachmentItem {
  id: string;
  name: string;
  originalName: string;
  fileUrl: string;
  width?: number | null;
  height?: number | null;
  duration?: string | number | null;
  fileSize?: number | null;
  mimeType?: string | null;
  sourceAttachmentId?: string | null;
  createdAt: string;
}

export interface AttachmentListResponse {
  items: AttachmentItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface UploadAttachmentPayload {
  file: File;
  category?: 'document' | 'image' | 'video' | 'audio';
  relatedId: string;
  relatedType?:
    | 'user'
    | 'project'
    | 'storyboard'
    | 'character'
    | 'location'
    | 'prop'
    | 'screenplay'
    | 'tag'
    | 'org';
  attachmentPurpose?:
    | 'avatar'
    | 'cover'
    | 'thumbnail'
    | 'document'
    | 'reference'
    | 'attachment'
    | 'source'
    | 'certification';
  sourceAttachmentId?: string;
  permissionProjectId?: string;
  onUploadProgress?: (progress: number, event: AxiosProgressEvent) => void;
}

export const attachmentsApi = {
  async upload(payload: UploadAttachmentPayload): Promise<AttachmentUploadResponse> {
    const category = payload.category ?? 'image';
    const relatedType = payload.relatedType ?? 'user';
    const attachmentPurpose = payload.attachmentPurpose ?? 'reference';
    const multipartConfig = await getUploadMultipartConfig();
    const isStoryboardReferenceMedia =
      relatedType === 'storyboard' &&
      attachmentPurpose === 'reference' &&
      (category === 'image' || category === 'audio' || category === 'video');
    const shouldUseMultipart =
      category === 'video' ||
      isStoryboardReferenceMedia ||
      shouldUseChunkedUpload(payload.file, multipartConfig);
    if (shouldUseMultipart) {
      const response = await uploadFileInChunks<AttachmentUploadResponse>({
        file: payload.file,
        businessType: 'attachment',
        multipartConfig,
        businessPayload: {
          category,
          relatedId: payload.relatedId,
          relatedType,
          attachmentPurpose,
          ...(payload.permissionProjectId ? { projectId: payload.permissionProjectId } : {}),
          ...(payload.sourceAttachmentId ? { sourceAttachmentId: payload.sourceAttachmentId } : {}),
        },
        onProgress: (progress) => {
          payload.onUploadProgress?.(progress, {} as AxiosProgressEvent);
        },
      });
      return {
        ...response,
        attachmentId: response.attachmentId ?? response.id,
        fullUrl: response.fullUrl ?? response.fileUrl,
        mimeType: response.mimeType ?? (payload.file.type || undefined),
        fileSize: response.fileSize ?? payload.file.size,
        width: response.width,
        height: response.height,
        duration: response.duration,
      };
    }

    const response = await uploadDirectToOss<AttachmentUploadResponse>({
      file: payload.file,
      businessType: 'attachment',
      businessPayload: {
        category,
        relatedId: payload.relatedId,
        relatedType,
        attachmentPurpose,
        ...(payload.permissionProjectId ? { projectId: payload.permissionProjectId } : {}),
        ...(payload.sourceAttachmentId ? { sourceAttachmentId: payload.sourceAttachmentId } : {}),
      },
      onProgress: (progress) => {
        payload.onUploadProgress?.(progress, {} as AxiosProgressEvent);
      },
    });
    return {
      ...response,
      attachmentId: response.attachmentId ?? response.id,
      fullUrl: response.fullUrl ?? response.fileUrl,
      mimeType: response.mimeType ?? (payload.file.type || undefined),
      fileSize: response.fileSize ?? payload.file.size,
      width: response.width,
      height: response.height,
      duration: response.duration,
    };
  },

  async listByRelated(params: {
    relatedId: string;
    relatedType: UploadAttachmentPayload['relatedType'];
    attachmentPurpose?: UploadAttachmentPayload['attachmentPurpose'];
    permissionProjectId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AttachmentListResponse> {
    const data = await apiClient.get<Record<string, unknown>>('/attachments', {
      params: {
        relatedId: params.relatedId,
        relatedType: params.relatedType,
        attachmentPurpose: params.attachmentPurpose,
        projectId: params.permissionProjectId,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 100,
      },
    });
    return keysToCamelCase<AttachmentListResponse>(data);
  },

  async delete(attachmentId: string): Promise<void> {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },
};
